import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { v4 as uuidv4 } from "uuid";

import DrawerField from "components/Drawer";
import DeviceRebootEvents from "./components/DeviceRebootEvents";
import PoEFaultDetection from "./components/PoEFaultDetection";
import UplinkStabilityStatus from "./components/UplinkStabilityStatus";
import SystemPerformanceBottleneck from "./components/SystemPerformanceBottleneck";
import FirmwareUpdateStatus from "./components/FirmwareUpdateStatus";

import useAuthScope from "hooks/useAuthScope";
import useDataSourceManager from "hooks/useDataSourceManager";
import Api from "./util/api";

const checkMockup = process.env.REACT_APP_IS_MOCKUP;

const Operations = () => {
  const { auth, isAuthReady } = useAuthScope();
  const { sendBatch } = useDataSourceManager();

  const [activeDrawer, setActiveDrawer] = useState(null); // 'reboot' | 'poe' | 'uplink' | 'performance' | 'firmware' | null
  const [loading, setLoading] = useState(true);

  const [deviceRebootData, setDeviceRebootData] = useState(null);
  const [poeErrorData, setPoeErrorData] = useState(null);
  const [uplinkInstabilityData, setUplinkInstabilityData] = useState(null);
  const [systemBottleneckData, setSystemBottleneckData] = useState(null);
  const [firmwareStatusData, setFirmwareStatusData] = useState(null);

  const [requestUuids] = useState({
    deviceReboot: uuidv4(),
    poeError: uuidv4(),
    uplinkInstability: uuidv4(),
    systemBottleneck: uuidv4(),
    firmwareStatus: uuidv4(),
  });

  const isAllZeros = (data, countKey) => {
    return !data || data[countKey] === 0;
  };

  const fetchData = async () => {
    if (checkMockup === "false" && (!auth.realm || (!auth.orgs && !auth.sites))) return;

    setLoading(true);
    const api = Api.init(auth);

    const deviceRebootParams = api.getDeviceRebootParams();
    const poeErrorParams = api.getPoEErrorParams();
    const uplinkInstabilityParams = api.getUplinkInstabilityParams();
    const systemBottleneckParams = api.getSystemBottleneckParams();
    const firmwareStatusParams = api.getFirmwareStatusParams();

    const { getSendBatchData } = await sendBatch([
      { ...deviceRebootParams, requestUuid: requestUuids.deviceReboot },
      { ...poeErrorParams, requestUuid: requestUuids.poeError },
      { ...uplinkInstabilityParams, requestUuid: requestUuids.uplinkInstability },
      { ...systemBottleneckParams, requestUuid: requestUuids.systemBottleneck },
      { ...firmwareStatusParams, requestUuid: requestUuids.firmwareStatus },
    ]);

    const rebootData = getSendBatchData({ requestUuid: requestUuids.deviceReboot })?.data;
    const poeData = getSendBatchData({ requestUuid: requestUuids.poeError })?.data;
    const uplinkData = getSendBatchData({ requestUuid: requestUuids.uplinkInstability })?.data;
    const performanceData = getSendBatchData({ requestUuid: requestUuids.systemBottleneck })?.data;
    const fwData = getSendBatchData({ requestUuid: requestUuids.firmwareStatus })?.data;

    if (rebootData) setDeviceRebootData(rebootData);
    if (poeData) setPoeErrorData(poeData);
    if (uplinkData) setUplinkInstabilityData(uplinkData);
    if (performanceData) setSystemBottleneckData(performanceData);
    if (fwData) setFirmwareStatusData(fwData);

    setLoading(false);
  };

  useEffect(() => {
    if (isAuthReady) {
      fetchData();
    }
  }, [isAuthReady]);

  const handleCloseDrawer = () => {
    setActiveDrawer(null);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <DeviceRebootEvents
        data={deviceRebootData}
        loading={loading}
        isEmpty={isAllZeros(deviceRebootData, 'deviceCount')}
        onClick={() => setActiveDrawer('reboot')}
      />
      <PoEFaultDetection
        data={poeErrorData}
        loading={loading}
        isEmpty={isAllZeros(poeErrorData, 'switchCount')}
        onClick={() => setActiveDrawer('poe')}
      />
      <UplinkStabilityStatus
        data={uplinkInstabilityData}
        loading={loading}
        isEmpty={isAllZeros(uplinkInstabilityData, 'deviceCount')}
        onClick={() => setActiveDrawer('uplink')}
      />
      <SystemPerformanceBottleneck
        data={systemBottleneckData}
        loading={loading}
        isEmpty={isAllZeros(systemBottleneckData, 'deviceCount')}
        onClick={() => setActiveDrawer('performance')}
      />
      <FirmwareUpdateStatus
        data={firmwareStatusData}
        loading={loading}
        isEmpty={isAllZeros(firmwareStatusData, 'deviceCount')}
        onClick={() => setActiveDrawer('firmware')}
      />

      {/* Device Reboot Drawer */}
      {activeDrawer === 'reboot' && (
        <DrawerField
          open={true}
          handleClose={handleCloseDrawer}
          width="550px"
          contentKey="DeviceRebootDrawer"
          commonContent="ainsightDeviceRebootDrawer"
          contentProps={{
            chartData: deviceRebootData,
            auth: auth
          }}
        />
      )}

      {/* PoE Fault Drawer */}
      {activeDrawer === 'poe' && (
        <DrawerField
          open={true}
          handleClose={handleCloseDrawer}
          width="550px"
          contentKey="PoEFaultDrawer"
          commonContent="ainsightPoEFaultDrawer"
          contentProps={{
            chartData: poeErrorData,
            auth: auth
          }}
        />
      )}

      {/* Uplink Stability Drawer */}
      {activeDrawer === 'uplink' && (
        <DrawerField
          open={true}
          handleClose={handleCloseDrawer}
          width="550px"
          contentKey="UplinkStabilityDrawer"
          commonContent="ainsightUplinkStabilityDrawer"
          contentProps={{
            chartData: uplinkInstabilityData,
            auth: auth
          }}
        />
      )}

      {/* System Performance Drawer */}
      {activeDrawer === 'performance' && (
        <DrawerField
          open={true}
          handleClose={handleCloseDrawer}
          width="550px"
          contentKey="SystemPerformanceDrawer"
          commonContent="ainsightSystemPerformanceDrawer"
          contentProps={{
            chartData: systemBottleneckData,
            auth: auth,
            onSuccess: fetchData
          }}
        />
      )}

      {/* Firmware Update Drawer */}
      {activeDrawer === 'firmware' && (
        <DrawerField
          open={true}
          handleClose={handleCloseDrawer}
          width="550px"
          contentKey="FirmwareUpdateDrawer"
          commonContent="ainsightFirmwareUpdateDrawer"
          contentProps={{
            chartData: firmwareStatusData,
            auth: auth
          }}
        />
      )}
    </Box>
  );
};

export default Operations;